import React, { useEffect, useState } from 'react';
import { AppBar, Avatar, Badge, Box, Breadcrumbs, Divider, IconButton, Menu, MenuItem, Toolbar, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OPENREMIT_LOGO_ALT, OPENREMIT_LOGO_SRC } from '../utils/brand';
interface TopBarProps {
  onMenuClick: () => void;
  onLogout: () => void;
  title?: string;
}
const routeLabels: Record<string, string> = {
  '/': 'Dashboard',
  '/transactions': 'Transactions',
  '/failed-tabs': 'Failed Transactions',
  '/rtgs': 'RTGS Transactions',
  '/transaction-reversal': 'Transaction Reversal',
  '/reversal-lists': 'Reversal Lists',
  '/eprc-generation': 'e-PRC Generation',
  '/compliance': 'Compliance Review',
  '/alert-configuration': 'Alert Configuration',
  '/nationality-block': 'Nationality Block',
  '/country-block': 'Nationality Block',
  '/purpose-payment-block': 'Purpose of Payment Block',
  '/checker-inbox-cancellation': 'Checker Cancellations',
  '/checker-inbox-amendment': 'Checker Amendments',
  '/checker-manual-settlement': 'Checker Manual Settlement',
  '/maker-manual-settlement': 'Maker Manual Settlement',
  '/partners': 'Partner Management',
  '/partner-users': 'Partner Users',
  '/sub-agents': 'Sub Agents',
  '/sub-agent-users': 'Sub Agent Users',
  '/branch-users/user-management': 'Branch Users',
  '/branch-users/file-upload': 'File Upload'
};
const OpenRemitLogo = () => <Box component="img" src={OPENREMIT_LOGO_SRC} alt={OPENREMIT_LOGO_ALT} sx={{
  height: 34,
  width: 'auto',
  display: 'block',
  flexShrink: 0
}} data-id="element-2468" data-mp="3jm8yg5pavvbp" />;
const TopBar: React.FC<TopBarProps> = ({
  onMenuClick,
  onLogout
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [clock, setClock] = useState(() => new Date());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const routeLabel = routeLabels[location.pathname] || 'OpenRemit';
  const greeting = clock.getHours() < 12 ? 'Good morning' : clock.getHours() < 17 ? 'Good afternoon' : 'Good evening';
  const toggleColorMode = () => window.dispatchEvent(new Event('openremit-toggle-color-mode'));
  const iconButtonSx = {
    color: 'text.secondary',
    backgroundColor: isDarkMode ? 'rgba(240,239,255,0.10)' : 'rgba(255,255,255,0.72)',
    border: '1px solid',
    borderColor: 'divider',
    width: 36,
    height: 36
  };
  return <AppBar position="fixed" color="inherit" elevation={0} sx={{
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: isDarkMode ? 'rgba(19,17,43,0.94)' : 'rgba(255,255,255,0.90)',
    backdropFilter: 'blur(18px)',
    zIndex: zTheme => zTheme.zIndex.drawer + 1,
    width: {
      xs: '100%',
      md: 'calc(100% - 240px)'
    },
    ml: {
      xs: 0,
      md: '240px'
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 2,
      background: 'linear-gradient(90deg, #5B4EF0 0%, #00B4B3 100%)'
    }
  }} data-id="element-2469" data-mp="28trs1hfooqfu">
      <Toolbar sx={{
      gap: 1.5
    }} data-id="element-2470" data-mp="2qc8dzvn3ofqy">
        {isMobile && <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={onMenuClick} sx={{
        mr: 1,
        color: 'primary.main'
      }} data-id="element-2471" data-mp="2q8uaul6m42ig"><Icon icon="lucide:menu" width={24} height={24} data-id="element-2472" data-mp="2zbd7gnjt1iw9" /></IconButton>}
        {isMobile && <OpenRemitLogo data-id="element-2473" data-mp="342vadh5e1tjv" />}
        <Box sx={{
        display: {
          xs: 'none',
          md: 'flex'
        },
        minWidth: 250,
        alignItems: 'center'
      }} data-id="element-2474" data-mp="342brny7wz14s"><Breadcrumbs separator={<Icon icon="lucide:chevron-right" width={14} height={14} data-id="element-2476" data-mp="390n48gplfpqp" />} aria-label="breadcrumb" data-id="element-2475" data-mp="3927uhu4vkmir"><Box component="button" onClick={() => navigate('/')} sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            border: 0,
            p: 0,
            color: 'primary.main',
            bgcolor: 'transparent',
            cursor: 'pointer',
            font: 'inherit'
          }} data-id="element-2477" data-mp="3e1ki4h3klxme"><Icon icon="lucide:home" width={14} height={14} data-id="element-2478" data-mp="jkk7bycdfqml" />Home</Box><Typography sx={{
            fontWeight: 600,
            fontSize: '0.84rem'
          }} data-id="element-2479" data-mp="en0h002efnea">{routeLabel}</Typography></Breadcrumbs></Box>
        <Box sx={{
        flexGrow: 1,
        display: 'flex',
        justifyContent: {
          xs: 'flex-start',
          md: 'center'
        }
      }} data-id="element-2480" data-mp="seosq28qk9uq"><Box sx={{
          display: {
            xs: 'none',
            sm: 'block'
          },
          textAlign: 'center'
        }} data-id="element-2481" data-mp="octpgs78vmeb"><Typography sx={{
            fontWeight: 700,
            fontSize: '0.88rem',
            lineHeight: 1.2
          }} data-id="element-2482" data-mp="oexu5ccywpfn">{greeting}, Admin</Typography><Typography sx={{
            color: 'text.secondary',
            fontSize: '0.7rem',
            lineHeight: 1.25
          }} data-id="element-2483" data-mp="ou282956e4h">{clock.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })} - {clock.toLocaleDateString([], {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            })}</Typography></Box></Box>
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2484" data-mp="1l2ubwkk55qsg">
          <Tooltip title={isDarkMode ? 'Light mode' : 'Dark mode'} arrow data-id="element-2485" data-mp="1l0ztpe098cs2"><IconButton size="small" onClick={toggleColorMode} sx={iconButtonSx} data-id="element-2486" data-mp="1g4j6qn4sk4gk"><Icon icon={isDarkMode ? 'lucide:sun' : 'lucide:moon'} width={18} height={18} data-id="element-2487" data-mp="1okrxmdtc3eby" /></IconButton></Tooltip>
          <Tooltip title="Help" arrow data-id="element-2488" data-mp="1smv8p1c3cgwp"><IconButton size="small" sx={iconButtonSx} data-id="element-2489" data-mp="1sn4zgr4yms3g"><Icon icon="lucide:help-circle" width={18} height={18} data-id="element-2490" data-mp="1sk0onawzoy3t" /></IconButton></Tooltip>
          <Tooltip title="Notifications" arrow data-id="element-2491" data-mp="1eoovh7yhffp0"><IconButton size="small" onClick={e => setNotificationAnchor(e.currentTarget)} sx={iconButtonSx} data-id="element-2492" data-mp="1jl5gvuearfye"><Badge badgeContent={3} color="error" data-id="element-2493" data-mp="27aq1ehq3ml9s"><Icon icon="lucide:bell" width={18} height={18} data-id="element-2494" data-mp="27ckr09cbf1k7" /></Badge></IconButton></Tooltip>
          <Tooltip title="Account settings" arrow data-id="element-2495" data-mp="2c91b8sge8vs0"><IconButton edge="end" aria-label="account of current user" onClick={e => setAnchorEl(e.currentTarget)} sx={{
            p: 0.5,
            border: `2px solid ${theme.palette.divider}`
          }} data-id="element-2496" data-mp="2cc5n8c03ovt8"><Avatar sx={{
              width: 32,
              height: 32,
              background: 'linear-gradient(135deg, #5B4EF0 0%, #00B4B3 100%)',
              fontWeight: 600
            }} data-id="element-2497" data-mp="22j89ej61zv0y">A</Avatar></IconButton></Tooltip>
        </Box>
      </Toolbar>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} data-id="element-2498" data-mp="2v1m1s38meosn"><Box sx={{
        px: 2,
        py: 1.5
      }} data-id="element-2499" data-mp="2zz15ae4wqz5n"><Typography variant="subtitle2" fontWeight={600} data-id="element-2500" data-mp="2zvn2x5w8upy7">Admin User</Typography><Typography variant="caption" color="text.secondary" data-id="element-2501" data-mp="2uxbsasy4kbex">admin@openremit.com</Typography></Box><Divider data-id="element-2502" data-mp="2qw02onf93q6q" /><MenuItem onClick={() => setAnchorEl(null)} data-id="element-2503" data-mp="2qw9sa9wdvvbw">Profile</MenuItem><MenuItem onClick={() => setAnchorEl(null)} data-id="element-2504" data-mp="2lxou0g0ozlq3">Settings</MenuItem><Divider data-id="element-2505" data-mp="2m01hf8p7yoaj" /><MenuItem onClick={onLogout} sx={{
        color: 'error.main'
      }} data-id="element-2506" data-mp="2lzrrtm836j5d">Logout</MenuItem></Menu>
      <Menu anchorEl={notificationAnchor} open={Boolean(notificationAnchor)} onClose={() => setNotificationAnchor(null)} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} PaperProps={{
      sx: {
        width: 320
      }
    }} data-id="element-2507" data-mp="2h55cj7yvw6g0"><Box sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between'
      }} data-id="element-2508" data-mp="2365p23n3xxlf"><Typography variant="subtitle2" fontWeight={600} data-id="element-2509" data-mp="1y7uetrswhl2o">Notifications</Typography><Typography variant="caption" color="primary" data-id="element-2510" data-mp="1yaqebkkecht8">Mark all as read</Typography></Box><Divider data-id="element-2511" data-mp="dq23zu7cdzp9" />{['New partner registration request', 'System maintenance scheduled', 'Compliance alert: Verification needed'].map((label, index) => <Box key={label} sx={{
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: index === 0 ? 'rgba(91,78,240,0.08)' : 'transparent'
      }} data-id="element-2512" data-mp="mqi2sqd54ins" data-mpi={index}><Typography variant="body2" fontWeight={index === 0 ? 600 : 400} data-id="element-2513" data-mp="rpbgaisfg9ny">{label}</Typography><Typography variant="caption" color="text.secondary" data-id="element-2514" data-mp="3r6jacre61lyy">{index === 0 ? '5 minutes ago' : index === 1 ? '2 hours ago' : '1 day ago'}</Typography></Box>)}</Menu>
    </AppBar>;
};
export default TopBar;